import type { StoreProduct } from "replo-runtime/shared/types";
import type { Product } from "schemas/generated/product";

import { filterNulls } from "replo-utils/lib/array";
import { exhaustiveSwitch, isNullish } from "replo-utils/lib/misc";

export function mapProductToStoreProduct(
  product: Product,
): StoreProduct | null {
  if (!product.externalId) {
    return null;
  }

  const images: string[] = filterNulls(
    product.media?.map((media) => {
      if (media.type === "image") {
        return media.url;
      }
      return null;
    }) ?? [],
  );

  const variants = product.variants.map((variant) => {
    const variantFeaturedImage = variant.featuredImage;
    const optionValuesByTitle = new Map(
      variant.optionValues.map((optVal) => [
        product.options.find((opt) => {
          return opt.optionValues.some((val) => val.id === optVal.id);
        })?.title,
        optVal.value,
      ]),
    );

    if (!variant.externalId || isNullish(variant.price)) {
      return null;
    }

    return {
      id: Number.parseInt(variant.externalId, 10),
      sku: variant.sku ?? null,
      title: variant.title ?? "",
      name: variant.title ?? "",
      available: true,
      price: String(variant.price),
      compare_at_price:
        variant.originalPrice !== null ? String(variant.originalPrice) : null,
      option1: optionValuesByTitle.get(product.options[0]?.title) ?? "",
      option2: optionValuesByTitle.get(product.options[1]?.title) ?? "",
      option3: optionValuesByTitle.get(product.options[2]?.title) ?? "",
      featured_image: variantFeaturedImage ?? null,
    };
  });

  const selling_plan_groups =
    product.sellingPlanGroups?.map((group) => ({
      app_id: group.appId,
      options: group.options.map((option, index) => ({
        name: option.name,
        position: index + 1,
        values: option.values,
      })),
      selling_plans: group.sellingPlans.map((plan) => ({
        id: Number(plan.id),
        name: plan.name,
        appName: group.appId,
        description: plan.description,
        options: plan.optionValues.map((opt, index) => ({
          name: opt.name,
          position: index + 1,
          value: opt.value,
        })),
        price_adjustments: plan.priceAdjustments.map((adj) => ({
          value: adj.value,
          value_type: exhaustiveSwitch({ type: adj.valueType })({
            percentage: "percentage" as const,
            fixedAmount: "fixed_amount" as const,
            price: "price" as const,
          }),
        })),
      })),
    })) ?? [];

  return {
    id: Number.parseInt(product.externalId, 10),
    title: product.title,
    description: product.description ?? "",
    handle: product.slug ?? "",
    type: product.type ?? "",
    vendor: product.vendor ?? "",
    status: exhaustiveSwitch({ type: product.status })({
      active: "ACTIVE" as const,
      archived: "ARCHIVED" as const,
      draft: "DRAFT" as const,
    }),
    images,
    featured_image:
      product.featuredMedia?.type === "image"
        ? product.featuredMedia.url
        : null,
    options: product.options.map((option) => option.title),
    variants: filterNulls(variants),
    selling_plan_groups,
  };
}

export function getProductImage(
  product: StoreProduct | Product,
): string | null {
  if ("featured_image" in product) {
    if (product.featured_image) {
      return product.featured_image;
    }
    return product.images[0] ?? null;
  }

  if (product.featuredMedia?.type === "image") {
    return product.featuredMedia.url;
  }

  if (product.media) {
    const firstImage = product.media.find((m) => m.type === "image");
    return firstImage?.url ?? null;
  }

  return null;
}
